import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import styles from './Hero.module.css';
import { scrollToSection } from '../../utils/scrollUtils';

import hero1 from '../../assets/images/hero-1.png';
import hero2 from '../../assets/images/hero-2.png';
import hero3 from '../../assets/images/hero-3.png';

const Hero = () => {
  const [currentSlide, setCurrentSlide] = useState(0);

  const backgroundImages = [hero1, hero2, hero3];

  // Auto-slide every 10 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % backgroundImages.length);
    }, 10000);

    return () => clearInterval(interval);
  }, [backgroundImages.length]);

  return (
    <section className={styles.hero} id="hero" data-section="hero">
      {/* Background Image Slider */}
      <div className={styles.backgroundSlider}>
        <AnimatePresence initial={false}>
          <motion.div
            key={currentSlide}
            className={styles.slideImage}
            style={{
              backgroundImage: `url(${backgroundImages[currentSlide]})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }}
            initial={{ opacity: 0, scale: 1.05 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 1.02 }}
            transition={{
              duration: 1.2,
              ease: [0.43, 0.13, 0.23, 0.96],
            }}
          />
        </AnimatePresence>

        {/* Dark overlay for text readability */}
        <div className={styles.overlay} />

        {/* Gradient overlay for better text contrast */}
        <div className={styles.gradientOverlay} />
      </div>

      {/* Slide Indicators */}
      <div className={styles.slideIndicators}>
        {backgroundImages.map((_, index) => (
          <button
            key={index}
            className={`${styles.indicator} ${currentSlide === index ? styles.activeIndicator : ''}`}
            onClick={() => setCurrentSlide(index)}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>

      {/* Main Content */}
      <div className={styles.container}>
        <motion.div
          className={styles.content}
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
        >
          <motion.span
            className={styles.badge}
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.5 }}
          >
            AI-Powered Credit Intelligence
          </motion.span>

          <motion.h1
            className={styles.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
          >
            Revolutionizing
            <span className={styles.titleAccent}> Credit Risk Management</span>
            <br />
            for Financial Inclusion in Africa
          </motion.h1>

          <motion.div
            className={styles.buttonGroup}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.8 }}
          >
            <button
              className={styles.primaryButton}
              onClick={() => scrollToSection('solution')}
            >
              Explore the Solution
            </button>
            <button
              className={styles.secondaryButton}
              onClick={() => scrollToSection('investment')}
            >
              Investment Opportunity
            </button>
          </motion.div>

          <motion.div
            className={styles.stats}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.9 }}
          >
            <div className={styles.stat}>
              <div className={styles.statValue}>60%</div>
              <div className={styles.statLabel}>NPL Reduction</div>
            </div>
            <div className={styles.statDivider} />
            <div className={styles.stat}>
              <div className={styles.statValue}>7 min</div>
              <div className={styles.statLabel}>Credit Decision</div>
            </div>
            <div className={styles.statDivider} />
            <div className={styles.stat}>
              <div className={styles.statValue}>92%</div>
              <div className={styles.statLabel}>AI Accuracy</div>
            </div>
          </motion.div>
        </motion.div>

        {/* Feature Highlights - Right Side */}
        <motion.div
          className={styles.featureHighlights}
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, delay: 0.5 }}
        >
        </motion.div>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        className={styles.scrollIndicator}
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{
          duration: 0.8,
          delay: 1.2,
          repeat: Infinity,
          repeatType: 'reverse',
          repeatDelay: 0.5
        }}
        onClick={() => scrollToSection('problem')}
      >
        <span className={styles.scrollText}>Scroll to explore</span>
        <div className={styles.scrollArrow}>↓</div>
      </motion.div>
    </section>
  );
};

export default Hero;